import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { globalPartsHistoryStartDate, menuPaperTopOffset } from '@/core/constants';
import { Popover, useTheme } from '@mui/material';
import { endOfDay, subMonths } from 'date-fns';
import { useMemo, useRef, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { DateRangePickerButton, DateRangePickerChevron, HotkeysHelperButton, NavButton } from './components';
import { ResetChip, ShortcutsContainer, StyledCaptionLabel, StyledDay, StyledMonthCaption, StyledNav, StyledWeekdays, createStyles, } from './DateRangePicker.styled';
import { defaultShortcutSet, pickerShortcuts } from './pickerShortcuts';
import { t } from '@/core/localization';
import { ru } from 'date-fns/locale/ru';
import { Chip } from '../Chip';
import { Stack } from '../Stack';
const DateRangePicker = ({ shortcuts: shortcutNames = defaultShortcutSet, clearable, withHelperButton, ...controlProps }) => {
    const theme = useTheme();
    const rdpCustomStyles = createStyles(theme);
    const [anchorElement, setAnchorElement] = useState(null);
    // We need to save current month before picker close to set stored month on open (that should be a default behavior,
    // but it isn't).
    // Moreover, the default value should be `previous month` (instead of current month) since the picker shows only a single month
    // instead of two months if current month is the last in acceptable range, even if `numberOfMonths` is set to `2` (that also
    // should be a default behavior, but it isn't).
    const lastSelectedMonth = useRef(subMonths(new Date(), 1));
    const { value, disabled, invalid, onChange } = controlProps;
    const shortcuts = useMemo(() => shortcutNames.map((shortcutName) => pickerShortcuts.find((item) => item.name === shortcutName) ?? pickerShortcuts[0]), [shortcutNames]);
    const handleMainButtonClick = (event) => {
        if (disabled)
            return;
        setAnchorElement(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorElement(null);
    };
    const handleShortcutChipClick = (shortcutName) => () => {
        if (!shortcuts)
            return;
        const shortcut = shortcuts.find((item) => item.name === shortcutName);
        onChange(shortcut?.getValue());
    };
    const handleResetChipClick = () => {
        onChange(undefined);
    };
    const handleSelect = (selected) => {
        onChange({ from: selected?.from, to: selected?.to ? endOfDay(selected.to) : selected?.to });
    };
    const handleMonthChange = (month) => {
        lastSelectedMonth.current = month;
    };
    const open = Boolean(anchorElement);
    return (_jsxs("div", { children: [_jsx(DateRangePickerButton, { selected: value, disabled: disabled, invalid: invalid, onClick: handleMainButtonClick }), _jsxs(Popover, { open: open, anchorEl: anchorElement, onClose: handleClose, anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'center',
                }, transformOrigin: {
                    vertical: 'top',
                    horizontal: 'center',
                }, slotProps: {
                    paper: {
                        sx: {
                            display: 'flex',
                            marginTop: menuPaperTopOffset,
                            padding: '1rem',
                            paddingBottom: '1.5rem',
                        },
                    },
                }, children: [_jsxs(ShortcutsContainer, { children: [withHelperButton && _jsx(HotkeysHelperButton, {}), _jsx(Stack, { direction: "column", spacing: 1, sx: {
                                    alignItems: 'flex-start',
                                }, children: shortcuts.map((shortcut) => (_jsx(Chip, { variant: "filled", color: "primary", size: "medium", label: shortcut.label, onClick: handleShortcutChipClick(shortcut.name) }, shortcut.name))) }), clearable && (_jsx(ResetChip, { variant: "filled", color: "default", size: "medium", label: t('date.rage.picker.shortcut.reset'), onClick: handleResetChipClick }))] }), _jsx(DayPicker
                    // required={!clearable}
                    , { 
                        // required={!clearable}
                        locale: ru, mode: "range", numberOfMonths: 2, 
                        // pagedNavigation // swap 2 months in one click
                        showOutsideDays: true, fixedWeeks: true, 
                        // TODO: use a variable, when localization is needed
                        weekStartsOn: 1, startMonth: globalPartsHistoryStartDate, endMonth: new Date(), selected: value, onSelect: handleSelect, onMonthChange: handleMonthChange, defaultMonth: lastSelectedMonth.current, styles: rdpCustomStyles, captionLayout: "dropdown-years", components: {
                            Nav: StyledNav,
                            CaptionLabel: StyledCaptionLabel,
                            PreviousMonthButton: ({ onClick }) => _jsx(NavButton, { icon: "prev", onClick: onClick }),
                            NextMonthButton: ({ onClick }) => _jsx(NavButton, { icon: "next", onClick: onClick }),
                            Weekdays: StyledWeekdays,
                            Day: StyledDay,
                            MonthCaption: StyledMonthCaption,
                            Chevron: DateRangePickerChevron,
                        } })] })] }));
};
export { DateRangePicker };
